import { create } from 'zustand';
import {
  getLibraryContent,
  getRecentUploads,
  getCategories,
  getContentDetails,
  ContentItem,
  RecentUpload,
  Category,
  ContentDetails,
} from '@/services/PagesServices/ContentManagementService';
import {
  createStandaloneLesson,
  getStandaloneLessons,
  getStandaloneLessonById,
  updateStandaloneLesson,
  deleteStandaloneLesson,
  StandaloneLessonData,
  StandaloneLessonResponse,
} from '@/services/SuperAdminLessonService';

interface ContentManagementState {
  libraryContent: ContentItem[] | null;
  recentUploads: RecentUpload[] | null;
  categories: Category[] | null;
  contentDetails: ContentDetails | null;
  standaloneLessons: StandaloneLessonResponse[] | null;
  selectedLesson: StandaloneLessonResponse | null;
  loading: boolean;
  error: string | null;

  fetchLibraryContent: () => Promise<void>;
  fetchRecentUploads: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  fetchContentDetails: (contentId: string) => Promise<void>;

  // Standalone lesson management
  createLesson: (lessonData: StandaloneLessonData) => Promise<StandaloneLessonResponse | null>;
  fetchStandaloneLessons: () => Promise<void>;
  fetchStandaloneLessonById: (lessonId: number) => Promise<void>;
  updateLesson: (lessonId: number, lessonData: StandaloneLessonData) => Promise<boolean>;
  deleteLesson: (lessonId: number) => Promise<boolean>;
}

const useSuperAdminContentManagementStore = create<ContentManagementState>((set, get) => ({
  libraryContent: null,
  recentUploads: null,
  categories: null,
  contentDetails: null,
  standaloneLessons: null,
  selectedLesson: null,
  loading: false,
  error: null,

  fetchLibraryContent: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getLibraryContent();
      set({ libraryContent: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchRecentUploads: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getRecentUploads();
      set({ recentUploads: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCategories: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCategories();
      set({ categories: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchContentDetails: async (contentId: string) => {
    try {
      set({ loading: true, error: null, contentDetails: null });
      const response = await getContentDetails(contentId);
      if (response.success && response.data) {
        set({ contentDetails: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to load content details', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Standalone Lesson Management Methods
  createLesson: async (lessonData: StandaloneLessonData) => {
    try {
      set({ loading: true, error: null });
      const response = await createStandaloneLesson(lessonData);

      if (response.success && response.data) {
        // Refresh the lessons list after creating
        get().fetchStandaloneLessons();
        set({ loading: false });
        return response.data;
      } else {
        set({
          error: response.error?.message || 'Failed to create lesson',
          loading: false
        });
        return null;
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return null;
    }
  },

  fetchStandaloneLessons: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getStandaloneLessons();

      if (response.success && response.data) {
        set({ standaloneLessons: response.data, loading: false });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch lessons',
          loading: false
        });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchStandaloneLessonById: async (lessonId: number) => {
    try {
      set({ loading: true, error: null, selectedLesson: null });
      const response = await getStandaloneLessonById(lessonId);

      if (response.success && response.data) {
        set({ selectedLesson: response.data, loading: false });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch lesson',
          loading: false
        });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateLesson: async (lessonId: number, lessonData: StandaloneLessonData) => {
    try {
      set({ loading: true, error: null });
      const response = await updateStandaloneLesson(lessonId, lessonData);

      if (response.success) {
        // Refresh the lessons list after updating
        get().fetchStandaloneLessons();
        set({ loading: false });
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to update lesson',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return false;
    }
  },

  deleteLesson: async (lessonId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await deleteStandaloneLesson(lessonId);

      if (response.success) {
        // Refresh the lessons list after deleting
        get().fetchStandaloneLessons();
        set({ loading: false });
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to delete lesson',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return false;
    }
  },
}));

export default useSuperAdminContentManagementStore;
